import React, { useCallback, useEffect, useMemo, useState } from 'react';

import {
  Checkbox,
  Col,
  Input,
  InputNumber,
  Row,
  Select,
  Switch,
  Table,
  TimePicker,
  Typography,
} from 'antd';
import { CheckOutlined, CloseOutlined } from '@ant-design/icons';

import moment from 'moment';
// eslint-disable-next-line import/no-extraneous-dependencies
import { v4 as uuidv4 } from 'uuid';

import { useTranslation } from 'react-i18next';
import { getFiles } from '../services/files';

import FileUploader from './FileUploader';
import useDatasource from '../hooks/useDatasource';
import DatePickerLocale from './DatePickerLocale';
import RangePickerLocale from './RangePickerLocale';
import { VALIDATOR_PARAMETER_TYPE } from './validators/parameters/constants';

const FormWrapper = ({ children }) => (
  <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32, xl: 40 }}>{children}</Row>
);

FormWrapper.Single = ({ children }) => (
  <Col xs={24} sm={24} md={10} lg={8} xl={8}>
    {children}
  </Col>
);

FormWrapper.Double = ({ children }) => (
  <Col xs={24} sm={24} md={14} lg={16} xl={16}>
    {children}
  </Col>
);

FormWrapper.ErrorMessage = ({ message }) => (
  <>
    {message && (
      <Typography.Paragraph
        type="danger"
        style={{ marginBottom: 0, fontStyle: 'italic' }}
      >
        {message}
      </Typography.Paragraph>
    )}
  </>
);

const FieldWrapper = ({ errorMessage, label, children }) => (
  <div className="form-group">
    {label && (
      <Typography.Paragraph style={{ marginBottom: 0 }}>
        {label}
      </Typography.Paragraph>
    )}
    {children}
    <FormWrapper.ErrorMessage message={errorMessage} />
  </div>
);

FormWrapper.InputTransformed = ({
  label,
  props,
  transformValue,
  transformOnChange,
}) => {
  const { invalid: errorMessage, value, onChange, ...restProps } = props;
  const transformedValue = transformValue ? transformValue(value) : value;

  const handleChange = (e) => {
    const newValue = e.target.value;
    if (onChange) {
      onChange({
        target: {
          value: transformOnChange ? transformOnChange(newValue) : newValue,
        },
      });
    }
  };

  return transformOnChange ? (
    <FieldWrapper label={label} errorMessage={errorMessage}>
      <Input {...restProps} value={transformedValue} onChange={handleChange} />
    </FieldWrapper>
  ) : (
    <FieldWrapper label={label} errorMessage={errorMessage}>
      <Input {...props} />
    </FieldWrapper>
  );
};

FormWrapper.Input = ({ label, props }) => {
  const { invalid: errorMessage } = props;
  return (
    <FieldWrapper label={label} errorMessage={errorMessage}>
      <Input {...props} />
    </FieldWrapper>
  );
};

const Search = ({ label, props }) => {
  const {
    invalid: errorMessage,
    getValue,
    name,
    fetcher,
    disabled,
    field,
    searchField,
    setValue,
    labelProp = 'name',
  } = props;

  const { loading, content, handleChange } = useDatasource(fetcher);
  return (
    <FieldWrapper label={label} errorMessage={errorMessage}>
      <Select
        showSearch
        loading={loading}
        disabled={disabled}
        value={getValue(name)}
        style={{ width: '100%' }}
        onChange={(val) => setValue(name, val)}
        onSearch={(filter) =>
          handleChange(null, { [searchField]: [filter] }, {})
        }
        filterOption={(input, option) =>
          option.children.toLowerCase().indexOf(input.toLowerCase()) !== -1
        }
      >
        {content.map((el) => (
          <Select.Option value={el.id} key={el[field]}>
            {typeof labelProp === 'string' ? el[labelProp] : labelProp(el)}
          </Select.Option>
        ))}
      </Select>
    </FieldWrapper>
  );
};

const StaticSearch = ({ label, props }) => {
  const {
    invalid: errorMessage,
    options = [],
    keyProp = 'id',
    labelProp = 'name',
    ...restProps
  } = props;

  return (
    <FieldWrapper label={label} errorMessage={errorMessage}>
      <Select
        {...restProps}
        showSearch
        filterOption={(input, option) =>
          option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
        }
      >
        {options.map((option) => (
          <Select.Option
            value={option[keyProp]}
            key={option[keyProp]}
            disabled={option.disabled || option.deleted}
          >
            {typeof labelProp === 'string'
              ? option[labelProp]
              : labelProp(option)}
          </Select.Option>
        ))}
      </Select>
    </FieldWrapper>
  );
};

FormWrapper.Password = ({ label, props }) => {
  const { invalid: errorMessage } = props;
  return (
    <FieldWrapper label={label} errorMessage={errorMessage}>
      <Input.Password {...props} />
    </FieldWrapper>
  );
};

FormWrapper.TextArea = ({ label, props }) => {
  const { invalid: errorMessage } = props;
  return (
    <FieldWrapper label={label} errorMessage={errorMessage}>
      <Input.TextArea {...props} />
    </FieldWrapper>
  );
};

FormWrapper.Number = ({ label, props }) => {
  const { name, invalid: errorMessage, onChange } = props;
  const handleChange = (value) => onChange(value, { currentTarget: { name } });

  return (
    <FieldWrapper label={label} errorMessage={errorMessage}>
      <InputNumber {...props} onChange={handleChange} />
    </FieldWrapper>
  );
};

const DateTimePicker = ({ label, props }) => {
  const {
    name,
    invalid: errorMessage,
    setValue,
    getValue,
    hideTime,
    toMidnightDown = false,
    toMidnightUp = false,
    ...restProps
  } = props;

  const handleChange = (val) => {
    if (!val) {
      setValue(name, null);
      return;
    }

    let finalValue;

    if (toMidnightDown) {
      // UTC midnight start of day
      finalValue = moment
        .utc([val.year(), val.month(), val.date()])
        .format('YYYY-MM-DDTHH:mm:ss.SSS[Z]');
    } else if (toMidnightUp) {
      // UTC end of day
      finalValue = moment
        .utc([val.year(), val.month(), val.date()])
        .endOf('day')
        .format('YYYY-MM-DDTHH:mm:ss.SSS[Z]');
    } else {
      // UTC with milliseconds and Z
      finalValue = val.utc().format('YYYY-MM-DDTHH:mm:ss.SSS[Z]');
    }

    setValue(name, finalValue);
  };

  const res = getValue(name);
  const value = res == null ? null : moment(res);

  return (
    <FieldWrapper label={label} errorMessage={errorMessage}>
      <DatePickerLocale
        format={`DD.MM.YYYY${hideTime ? '' : ' HH:mm'}`}
        showTime={!hideTime}
        onChange={handleChange}
        value={value}
        style={{ width: '100%' }}
        {...restProps}
      />
    </FieldWrapper>
  );
};

const RangePicker = ({ label, props }) => {
  const { start, end, ...restProps } = props;

  const handleChange = ([startDate, endDate]) => {
    start.setValue(start.name, startDate);
    end.setValue(end.name, endDate);
  };
  const value = useMemo(() => {
    const startDate = start.getValue(start.name);
    const endDate = end.getValue(end.name);
    return [
      startDate ? moment(startDate) : null,
      endDate ? moment(endDate) : null,
    ];
  }, [end, start]);

  return (
    <FieldWrapper
      label={label}
      errorMessage={[start.invalid, end.invalid].join(' ')}
    >
      <RangePickerLocale
        format="DD.MM.YYYY HH:mm"
        showTime={{ format: 'HH:mm' }}
        onChange={handleChange}
        value={value}
        style={{ width: '100%' }}
        {...restProps}
      />
    </FieldWrapper>
  );
};

const TimeRangePicker = ({ label, props }) => {
  const { start, end, ...restProps } = props;

  const handleChange = useCallback(
    (range) => {
      start.setValue(start.name, range ? range[0] : null);
      end.setValue(end.name, range ? range[1] : null);
    },
    [end, start],
  );

  const value = useMemo(() => {
    const startDate = start.getValue(start.name);
    const endDate = end.getValue(end.name);
    return [
      startDate ? moment(startDate) : null,
      endDate ? moment(endDate) : null,
    ];
  }, [end, start]);

  return (
    <FieldWrapper
      label={label}
      errorMessage={[start.invalid, end.invalid].join(' ')}
    >
      <TimePicker.RangePicker
        onChange={handleChange}
        value={value}
        style={{ width: '100%' }}
        {...restProps}
      />
    </FieldWrapper>
  );
};

const BitMaskCheckBoxes = ({ label, maskType, props }) => {
  const { t } = useTranslation();
  const { name, invalid: errorMessage, setValue, getValue, disabled } = props;
  const { type } = maskType;
  const decimalValueString = getValue(name);
  const numberOfBits = 8;
  const [switches, setSwitches] = useState(Array(numberOfBits).fill(false));
  const [bitLabels, setBitLabels] = useState([]);

  const setCheckboxesFromDecimal = (decimal) => {
    const binaryString = decimal.toString(2).padStart(numberOfBits, '0');
    const newSwitches = binaryString.split('').map((bit) => bit === '1');
    setSwitches(newSwitches);
  };

  const handleSwitchChange = (index) => {
    const updatedSwitches = [...switches];
    updatedSwitches[index] = !updatedSwitches[index];
    setSwitches(updatedSwitches);
  };

  useEffect(() => {
    switch (type) {
      case VALIDATOR_PARAMETER_TYPE.UBYTE_MASK_SERVICE_STATE:
        setBitLabels([
          // am inversat ordinea: index 0 ajunge fizic bit 0 (din cauza invertEndian)
          t(
            'entity.taxonomy.validators.parameters.bitmask.serviceState.control',
          ), // bit 0 (index 0)
          t(
            'entity.taxonomy.validators.parameters.bitmask.serviceState.statistics',
          ),
          t(
            'entity.taxonomy.validators.parameters.bitmask.serviceState.transshipment',
          ),
          t(
            'entity.taxonomy.validators.parameters.bitmask.serviceState.commercial',
          ),
          t(
            'entity.taxonomy.validators.parameters.bitmask.serviceState.notUsed',
          ),
          t(
            'entity.taxonomy.validators.parameters.bitmask.serviceState.access',
          ),
          t(
            'entity.taxonomy.validators.parameters.bitmask.serviceState.garage',
          ),
          t(
            'entity.taxonomy.validators.parameters.bitmask.serviceState.closed',
          ), // bit 7 (index 7)
        ]);
        return;
      case VALIDATOR_PARAMETER_TYPE.UBYTE_MASK_DAYS:
        setBitLabels([
          t('entity.taxonomy.validators.parameters.bitmask.days.publicHoliday'),
          t('entity.taxonomy.validators.parameters.bitmask.days.sunday'),
          t('entity.taxonomy.validators.parameters.bitmask.days.monday'),
          t('entity.taxonomy.validators.parameters.bitmask.days.tuesday'),
          t('entity.taxonomy.validators.parameters.bitmask.days.wednesday'),
          t('entity.taxonomy.validators.parameters.bitmask.days.thursday'),
          t('entity.taxonomy.validators.parameters.bitmask.days.friday'),
          t('entity.taxonomy.validators.parameters.bitmask.days.saturday'),
        ]);
        return;
      default:
        setBitLabels([
          t('entity.taxonomy.validators.parameters.bitmask.byte.7'),
          t('entity.taxonomy.validators.parameters.bitmask.byte.6'),
          t('entity.taxonomy.validators.parameters.bitmask.byte.5'),
          t('entity.taxonomy.validators.parameters.bitmask.byte.4'),
          t('entity.taxonomy.validators.parameters.bitmask.byte.3'),
          t('entity.taxonomy.validators.parameters.bitmask.byte.2'),
          t('entity.taxonomy.validators.parameters.bitmask.byte.1'),
          t('entity.taxonomy.validators.parameters.bitmask.byte.0'),
        ]);
    }
  }, [type, t]);

  useEffect(() => {
    if (decimalValueString) {
      const decimalValue = parseInt(decimalValueString, 10);
      setCheckboxesFromDecimal(decimalValue);
    }
  }, [decimalValueString, getValue, name]);

  useEffect(() => {
    const decimalResult = switches.reduce((bitmask, checked, index) => {
      const bitPosition = switches.length - 1 - index;
      // eslint-disable-next-line no-bitwise
      return checked ? bitmask | (1 << bitPosition) : bitmask;
    }, 0);
    setValue(name, decimalResult);
  }, [getValue, name, setValue, switches]);

  return (
    <FieldWrapper label={label} errorMessage={errorMessage}>
      <div
        key="bit-mask"
        style={{
          display: 'flex',
          flexDirection: 'row',
          gap: '5px',
        }}
      >
        {switches.map((checked, index) => (
          <FieldWrapper
            key={uuidv4()}
            label={bitLabels[index]}
            errorMessage={errorMessage}
          >
            <Switch
              checked={checked}
              disabled={disabled}
              onChange={() => handleSwitchChange(index)}
              checkedChildren={<CheckOutlined />}
              unCheckedChildren={<CloseOutlined />}
            />
          </FieldWrapper>
        ))}
      </div>
    </FieldWrapper>
  );
};

FormWrapper.Switch = ({ label, props }) => {
  const { invalid: errorMessage } = props;
  return (
    <FieldWrapper label={label} errorMessage={errorMessage}>
      <Switch
        checkedChildren={<CheckOutlined />}
        unCheckedChildren={<CloseOutlined />}
        {...props}
      />
    </FieldWrapper>
  );
};

FormWrapper.CheckBox = ({ label, props }) => {
  const { invalid: errorMessage } = props;
  return (
    <>
      <Checkbox {...props}>{label}</Checkbox>
      <FormWrapper.ErrorMessage message={errorMessage} />
    </>
  );
};

FormWrapper.Select = ({ label, props }) => {
  const {
    invalid: errorMessage,
    options = [],
    keyProp = 'id',
    labelProp = 'name',
    ...restProps
  } = props;

  return (
    <FieldWrapper label={label} errorMessage={errorMessage}>
      <Select {...restProps}>
        {options.map((option) => (
          <Select.Option
            value={option[keyProp]}
            key={option[keyProp]}
            disabled={option.disabled || option.deleted}
          >
            {typeof labelProp === 'string'
              ? option[labelProp]
              : labelProp(option)}
          </Select.Option>
        ))}
      </Select>
    </FieldWrapper>
  );
};

FormWrapper.GroupedSelect = ({ label, props }) => {
  const {
    invalid: errorMessage,
    keyProp = 'id',
    labelProp = 'name',
    groupedOptions = [],
    ...restProps
  } = props;
  return (
    <FieldWrapper label={label} errorMessage={errorMessage}>
      <Select {...restProps}>
        {groupedOptions.map((groupedOption) => (
          <Select.OptGroup
            label={groupedOption.label}
            key={groupedOption.label}
          >
            {groupedOption.options.map((option) => (
              <Select.Option
                value={option[keyProp]}
                key={option[keyProp]}
                disabled={option.disabled}
              >
                {typeof labelProp === 'string'
                  ? option[labelProp]
                  : labelProp(option)}
              </Select.Option>
            ))}
          </Select.OptGroup>
        ))}
      </Select>
    </FieldWrapper>
  );
};

const FileList = ({ props }) => {
  const {
    name,
    setValue,
    getValue,
    defaultFileProps,
    tableProps,
    allowUpload = true,
  } = props;

  const notFetchedFileIds = getValue(name)
    ?.filter((file) => !file.fetched)
    ?.map((file) => file.id);

  useEffect(() => {
    if (notFetchedFileIds.length > 0)
      getFiles(notFetchedFileIds).then((fetchedFiles) =>
        setValue(
          name,
          getValue(name).reduce(
            (acc, cur) => [
              ...acc,
              {
                ...cur,
                ...fetchedFiles.find((file) => file.id === cur.id),
                fetched: true,
              },
            ],
            [],
          ),
        ),
      );
  }, [getValue, name, notFetchedFileIds, setValue]);

  const addFiles = useCallback(
    (files) =>
      setValue(name, [
        ...getValue(name),
        ...files.map((file) => ({
          ...file,
          ...defaultFileProps,
          fetched: true,
        })),
      ]),
    [defaultFileProps, getValue, name, setValue],
  );

  return (
    <>
      {allowUpload && (
        <div className="form-group">
          <FileUploader onUploadSuccess={addFiles} />
        </div>
      )}
      <div className="form-group">
        <Table
          pagination={false}
          dataSource={getValue(name)}
          scroll={{ x: 700, y: 700 }}
          {...tableProps}
        />
      </div>
    </>
  );
};

FormWrapper.FileUploader = ({ props }) => {
  const {
    invalid: errorMessage,
    name,
    setValue,
    getValue,
    setFieldValue,
    onUploadFailed,
    buttonType,
  } = props;
  return (
    <>
      <FileUploader
        {...props}
        buttonType={buttonType}
        dragger={false}
        onUploadSuccess={(files, blob) => {
          setValue(name, files);
          if (getValue !== undefined)
            setFieldValue('docMap', {
              ...getValue('docMap'),
              [files[0].id]: blob,
            });
        }}
        onUploadFailed={onUploadFailed}
      />
      <FormWrapper.ErrorMessage message={errorMessage} />
    </>
  );
};

FormWrapper.RangePicker = RangePicker;
FormWrapper.TimeRangePicker = TimeRangePicker;
FormWrapper.DateTimePicker = DateTimePicker;
FormWrapper.FileList = FileList;
FormWrapper.FieldWrapper = FieldWrapper;
FormWrapper.Search = Search;
FormWrapper.StaticSearch = StaticSearch;
FormWrapper.BitMaskCheckBoxes = BitMaskCheckBoxes;

export default FormWrapper;
